import React, { useMemo } from "react";

import { useSoilSightCurrency, useTranslation } from "utils";

import { COMPARE_COLUMN_WIDTHS, COMPARE_SCENARIO_GROUP_WIDTH_BASELINE, COMPARE_SCENARIO_GROUP_WIDTH_COMPARISON } from "consts";

import { Button, Table, Tooltip } from "components";

import { RotationCycleMarker } from "pages/app/scenario-analysis/components/detailed-calculations/RotationCycleMarker";
import { ScenarioCalculationEntry } from "pages/app/scenario-analysis/hooks/useScenarioCalculationEntries";
import { ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { DetailedCalculationRow, formatPerHectare as money } from "pages/app/scenario-analysis/utils/detailedCalculations";

type CompareTableProps = {
  entries: ScenarioCalculationEntry[];
  /** Entry used as the delta reference; the first baseline scenario, else the first entry. */
  baselineEntry: ScenarioCalculationEntry;
  mainCropLabelLookup: Record<string, string>;
  onShowDetail: (scenarioId: string) => void;
  /** Resolved against the full scenario list, so filtering the table never reshuffles colours. */
  scenarioColor: (scenarioId: string) => string;
  /** Caps the body height so the header row stays pinned while the years scroll. */
  scrollY?: number | string;
  dataTestId?: string;
};

type CompareRow = { key: number; calendarYear: number; byScenario: Record<string, DetailedCalculationRow | undefined>; cumulatives: Record<string, number> };

const asCompareRow = (record: object) => record as CompareRow;

const PREFIX = "detailedCalculations.";

/** Condensed side-by-side overview: one column group per scenario, delta against the baseline. */
export const CompareTable = ({
  entries,
  baselineEntry,
  mainCropLabelLookup,
  onShowDetail,
  scenarioColor,
  scrollY,
  dataTestId = "compare-table",
}: CompareTableProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { currencySymbol } = useSoilSightCurrency();

  // No symbol: the headers carry the unit and CM/Cum. CM print bare numbers.
  const signedMoney = (value: number) => `${value >= 0 ? "+" : ""}${money(value)}`;
  const perHa = `${currencySymbol}/ha`;

  const rowCount = entries.reduce((longest, entry) => Math.max(longest, entry.data.rows.length), 0);
  const minWidth =
    COMPARE_COLUMN_WIDTHS.year +
    entries.reduce((total, { scenario }) => total + (scenario.isBaseline ? COMPARE_SCENARIO_GROUP_WIDTH_BASELINE : COMPARE_SCENARIO_GROUP_WIDTH_COMPARISON), 0);

  const cropLabel = (cropId: string | null) => (cropId ? mainCropLabelLookup[cropId] ?? "—" : "—");

  const rows = useMemo<CompareRow[]>(
    () =>
      Array.from({ length: rowCount }, (_, rowIndex) => ({
        key: rowIndex,
        calendarYear: baselineEntry.data.rows[rowIndex]?.calendarYear ?? rowIndex + 1,
        byScenario: Object.fromEntries(entries.map(({ scenario, data }) => [scenario.id, data.rows[rowIndex]])),
        cumulatives: Object.fromEntries(entries.map(({ scenario, data }) => [scenario.id, data.cumulatives[rowIndex] ?? 0])),
      })),
    [entries, baselineEntry, rowCount]
  );

  const columnLabel = (labelKey: string) => {
    const label = t(`${PREFIX}column.${labelKey}`);
    return (
      <>
        <Tooltip title={label}>
          <span className="compare-table-col-label">{label}</span>
        </Tooltip>
        <span className="compare-table-col-unit">{perHa}</span>
      </>
    );
  };

  const scenarioHeading = (scenario: ScenarioData) => (
    <span className="compare-table-scenario-heading" data-testid={`${dataTestId}-group-${scenario.id}`}>
      <span className="compare-table-scenario-dot" style={{ backgroundColor: scenarioColor(scenario.id) }} aria-hidden />
      <span className="compare-table-scenario-name">{scenario.title}</span>
      {scenario.isBaseline ? <span className="compare-table-baseline-badge">{t(`${PREFIX}baseline`)}</span> : null}
      <Button
        type="link"
        size="small"
        className="compare-table-details-link"
        onClick={() => onShowDetail(scenario.id)}
        dataTestId={`${dataTestId}-details-${scenario.id}`}
      >
        {t(`${PREFIX}showDetails`)}
      </Button>
    </span>
  );

  const deltaFor = (row: CompareRow, scenarioId: string) => {
    const scenarioRow = row.byScenario[scenarioId];
    const baselineRow = row.byScenario[baselineEntry.scenario.id];
    return scenarioRow && baselineRow ? scenarioRow.cmMid - baselineRow.cmMid : null;
  };

  const columns = [
    {
      title: t(`${PREFIX}column.year`),
      key: "year",
      fixed: "left" as const,
      width: COMPARE_COLUMN_WIDTHS.year,
      className: "compare-table-year-cell",
      render: (_: unknown, record: object) => asCompareRow(record).calendarYear,
    },
    ...entries.map(({ scenario }) => ({
      title: scenarioHeading(scenario),
      key: scenario.id,
      // Scenario colour rule under the group heading, matching its chart line.
      onHeaderCell: () => ({ style: { boxShadow: `inset 0 -0.125rem 0 ${scenarioColor(scenario.id)}` } }),
      children: [
        {
          title: t(`${PREFIX}column.crop`),
          key: `${scenario.id}-crop`,
          width: COMPARE_COLUMN_WIDTHS.crop,
          className: "compare-table-crop-cell",
          render: (_: unknown, record: object) => {
            const row = asCompareRow(record).byScenario[scenario.id];
            if (!row) return null;
            return (
              <span className="compare-table-crop">
                {row.isNewCycle ? <RotationCycleMarker className="compare-table-cycle-marker" /> : null}
                <span className="compare-table-crop-label">{cropLabel(row.cropId)}</span>
              </span>
            );
          },
        },
        {
          title: columnLabel("cmMid"),
          key: `${scenario.id}-cm`,
          width: COMPARE_COLUMN_WIDTHS.cmMid,
          className: "compare-table-cm",
          render: (_: unknown, record: object) => {
            const row = asCompareRow(record).byScenario[scenario.id];
            return row ? money(row.cmMid) : null;
          },
        },
        {
          title: columnLabel("cumulativeCm"),
          key: `${scenario.id}-cumulative`,
          width: COMPARE_COLUMN_WIDTHS.cumulativeCm,
          className: `compare-table-cumulative${scenario.isBaseline ? " compare-table-group-end" : ""}`,
          render: (_: unknown, record: object) => {
            const compareRow = asCompareRow(record);
            return compareRow.byScenario[scenario.id] ? money(compareRow.cumulatives[scenario.id] ?? 0) : null;
          },
        },
        ...(scenario.isBaseline
          ? []
          : [
              {
                title: columnLabel("deltaVsBaseline"),
                key: `${scenario.id}-delta`,
                width: COMPARE_COLUMN_WIDTHS.delta,
                className: "compare-table-delta compare-table-group-end",
                render: (_: unknown, record: object) => {
                  const delta = deltaFor(asCompareRow(record), scenario.id);
                  if (delta == null) return null;
                  return <span className={delta < 0 ? "compare-table-negative" : undefined}>{signedMoney(delta)}</span>;
                },
              },
            ]),
      ],
    })),
  ];

  const renderTotals = () => {
    // A baseline group has three columns and a comparison group four, so the index has to run.
    let index = 0;
    const nextIndex = () => {
      index += 1;
      return index;
    };

    return (
      <Table.Summary.Row className="compare-table-total-row">
        <Table.Summary.Cell index={0} className="compare-table-total-label">
          {t(`${PREFIX}totalYears`, { count: rowCount })}
        </Table.Summary.Cell>
        {entries.flatMap(({ scenario, data }) => {
          const totalDelta = data.totals.cmMid - baselineEntry.data.totals.cmMid;
          return [
            <Table.Summary.Cell key={`${scenario.id}-crop`} index={nextIndex()} />,
            <Table.Summary.Cell key={`${scenario.id}-cm`} index={nextIndex()} className="compare-table-cm">
              {money(data.totals.cmMid)}
            </Table.Summary.Cell>,
            <Table.Summary.Cell key={`${scenario.id}-cumulative`} index={nextIndex()} className="compare-table-cumulative">
              {money(data.cumulatives[data.cumulatives.length - 1] ?? 0)}
            </Table.Summary.Cell>,
            ...(scenario.isBaseline
              ? []
              : [
                  <Table.Summary.Cell key={`${scenario.id}-delta`} index={nextIndex()} className="compare-table-delta">
                    <span className={totalDelta < 0 ? "compare-table-negative" : undefined}>{signedMoney(totalDelta)}</span>
                  </Table.Summary.Cell>,
                ]),
          ];
        })}
      </Table.Summary.Row>
    );
  };

  return (
    <div className="compare-table" data-testid={dataTestId}>
      <Table<CompareRow>
        className="compare-table-grid"
        data={rows}
        columns={columns}
        rowKey="key"
        pagination={false}
        scroll={{ x: minWidth, y: scrollY }}
        summary={renderTotals}
      />
    </div>
  );
};

export default CompareTable;
